import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Armchair, Lock, Phone } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(mobile.trim(), password);
      toast.success("Welcome back!");
      window.location.href = "/";
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-white"
      data-testid="login-page"
    >
      {/* Left: form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-12 w-auto">
              <img
                src="/adams-logo.jpeg"
                alt="Adams"
                className="h-12 w-auto object-contain"
              />
            </div>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-950">
            Sign in to your console
          </h1>
          <p className="mt-3 text-slate-600">
            Manage orders, logistics and daily cash for your event furniture
            rental business.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="mobile" className="text-slate-700">
                Mobile Number
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="mobile"
                  data-testid="login-mobile-input"
                  inputMode="numeric"
                  placeholder="9243425500"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  className="pl-9 h-11"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 h-11"
                  required
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md"
            >
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-slate-500">
            Authorized staff only. Contact owner for access.
          </p>
        </div>
      </div>

      {/* Right: Brand panel */}
      <div className="hidden lg:flex relative bg-red-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="grid"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="white"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative p-16 flex flex-col justify-between w-full">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold opacity-80">
              Adams · Operations Desktop
            </div>
            <h2 className="font-heading text-5xl font-extrabold tracking-tight mt-6 leading-tight">
              Every order.
              <br />
              Every delivery.
              <br />
              <span className="underline decoration-4 decoration-white/40 underline-offset-8">
                One place.
              </span>
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-12">
            <Metric k="AI scan" v="Order forms" />
            <Metric k="Live" v="Logistics" />
            <Metric k="Daily" v="Net cash" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.2em] opacity-70">{k}</div>
      <div className="font-heading text-xl font-bold mt-1">{v}</div>
    </div>
  );
}
